import React from 'react';
import { Building2 } from 'lucide-react';
import Banner from 'components/crm/ui/Banner';
import Button from 'components/crm/ui/Button';
import { useCrmLocale } from 'components/crm/ui/crmTime';
import { formatSiret } from 'helper/companyDirectory';
import useText from 'hooks/useText';

// Acronym, identical in every language.
const SIRET_PREFIX = 'SIRET';

// « 1er octobre », « 15 octobre » ; « October 1 » in English.
export const followedSinceLabel = (since, locale = 'fr') => {
  const date = new Date(since);
  if (Number.isNaN(date.getTime())) return '';
  const month = new Intl.DateTimeFormat(locale, { month: 'long' }).format(date);
  const day = date.getDate();
  if (locale === 'en') return `${month} ${day}`;
  return `${day === 1 ? '1er' : day} ${month}`;
};

export const CompanyCard = ({ company, onChange }) => {
  const { text } = useText('prospection.addProspect.company');
  const line = [
    company.legalForm,
    company.siret && `${SIRET_PREFIX} ${formatSiret(company.siret)}`,
    company.activity,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <div className="flex items-center gap-3 rounded-xl bg-surfaceMuted p-[14px]">
      <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-white text-ink">
        <Building2 size={20} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-ink">
          {company.name}
        </p>
        <p className="truncate text-xs text-muted">
          {line || text('manualLine')}
        </p>
      </div>
      <Button variant="ghost" onClick={onChange}>
        {text('change')}
      </Button>
    </div>
  );
};

// A company already in Jobbiz: followed by another salesperson, by the user,
// or a client nobody follows (`followedBy: null`).
const FollowedCompany = ({ company, currentUserId, onChange }) => {
  const { text } = useText('prospection.addProspect.followed');
  const locale = useCrmLocale();
  const { followed } = company;
  const followedBy = followed.followedBy ?? null;
  const isSelf =
    Boolean(followedBy?.id) &&
    Boolean(currentUserId) &&
    String(followedBy.id) === String(currentUserId);
  const values = {
    name: followed.name || company.name,
    city: followed.city || company.city || '',
    firstname: followedBy?.firstname ?? '',
    date: followedSinceLabel(followed.since, locale),
  };
  let bannerKey = 'banner';
  if (!followedBy) bannerKey = 'bannerClient';
  else if (isSelf) bannerKey = 'bannerSelf';
  // Without a city the « ({city}) » of the text would stay as empty brackets.
  const banner = text(bannerKey, values).replace(' ()', '');
  const canAskOwner = Boolean(followedBy) && !isSelf;

  return (
    <>
      <Banner tone="warning">{banner}</Banner>
      <CompanyCard company={company} onChange={onChange} />
      <p className="text-sm text-body">
        {canAskOwner ? text('text', values) : text('textOpen')}
      </p>
    </>
  );
};

export default FollowedCompany;
